import { bindSvgImageThemes } from '../utils/svg-images.js';
import { html, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from '../vendor/preact-htm.js';
import { useTranslation } from '../utils/i18n.js';
import { getMediaInfo, getMediaUrl, getThumbnailUrl, submitAdaptiveCardAction } from '../api.js';
import { renderMarkdown, renderMermaidDiagrams, renderThinkingMarkdown, sanitizeUrl } from '../markdown.js';
import { useChatProjectRepository } from '../ui/use-chat-project.js';
import { decodeSvgSource } from '../utils/svg-images.js';
import { formatCount, formatFileSize, formatTime, formatTimestamp } from '../utils/format.js';
import { buildPostMarkdownCopyPayload } from '../utils/post-copy-markdown.js';
import { DEFAULT_AGENT_NAME, getAvatarInfo } from '../ui/agent-utils.js';
import { getAttachmentPreviewKind, getAttachmentPreviewLabel } from '../ui/attachment-preview.js';
import { extractCardBlocks, renderAdaptiveCard } from '../ui/adaptive-card-renderer.js';
import { buildAdaptiveCardSubmissionFallbackText, describeAdaptiveCardSubmission, extractAdaptiveCardSubmissionBlocks } from '../ui/adaptive-card-submission.js';
import { buildGeneratedWidgetPayload, canRenderGeneratedWidget } from '../ui/generated-widget.js';
import { disclosureTriangleSvgString, renderDisclosureTriangle } from '../ui/disclosure-triangle.js';
import { isIOSDevice } from '../ui/app-helpers.js';
import { attachHeaderAnchor } from '../ui/scroll-anchor.js';
import { presentProviderAuth } from '../ui/provider-auth-presentation.js';
import { ImageModal } from './image-modal.js';
import { ImageAnnotator, canAnnotate } from './image-annotator.js';
import { FilePill } from './file-pill.js';
import { isSafeUploadFilename } from '../../../src/core/upload-limits.js';
import {
    applyHighlightsToElement,
    applyAsidesToElement,
    extractHighlightsFromAnnotations,
    extractAsidesFromAnnotations,
    getSelectionInElement,
    HIGHLIGHT_COLORS,
    persistHighlight,
    persistAside,
    buildHighlightFromSelectionSnapshot,
    removeAnnotationAtIndex,
    resolveHighlightPopupPlacement,
    hasCoarseAnnotationPointer,
    subscribePostSelectionChanges,
    type PostAside,
} from './post-highlights.js';
import { buildSpeakablePostText, getSpeechPlaybackState, isSpeechSynthesisSupported, speakPostText, stopSpeechPlayback, subscribeSpeechPlayback } from './post-speech.ts';
import { copyPlainTextSelectionFromElement, readSessionStorageFlagBestEffort, resolveLinkPreviewSiteName, writeClipboardDataViaExecCommand, writeClipboardTextBestEffort, writeSessionStorageFlagBestEffort } from './post-runtime-safety.js';

/**
 * File attachment component - keeps single-click download on the main card while
 * exposing an explicit preview affordance for the v1 preview flow.
 */
function FileAttachment({ mediaId, onPreview, allowActions = true, loadInfo = getMediaInfo }) {
    const [info, setInfo] = useState(null);

    useEffect(() => {
        let cancelled = false;
        Promise.resolve().then(() => loadInfo(mediaId)).then((value) => {
            if (!cancelled) setInfo(value);
        }).catch((error) => {
            if (!cancelled) console.warn('[post] Failed to load attachment metadata for file card:', mediaId, error);
        });
        return () => { cancelled = true; };
    }, [loadInfo, mediaId]);

    if (!info) return null;

    const filename = typeof info?.filename === 'string' && info.filename.trim() ? info.filename.trim() : 'file';
    const contentType = typeof info?.content_type === 'string' ? info.content_type : '';
    const size = typeof info?.metadata?.size === 'number' && Number.isFinite(info.metadata.size) && info.metadata.size > 0
        ? info.metadata.size
        : null;
    const sizeStr = size ? formatFileSize(size) : '';
    const previewKind = getAttachmentPreviewKind(contentType, filename);
    const canPreview = allowActions && previewKind !== 'unsupported' && typeof onPreview === 'function';
    return html`
        <div class="file-attachment" onClick=${(e) => e.stopPropagation()}>
            <a
                href=${allowActions ? getMediaUrl(mediaId) : undefined}
                download=${allowActions ? filename : undefined}
                class="file-attachment-main"
                aria-disabled=${allowActions ? undefined : 'true'}
                onClick=${allowActions ? undefined : (event) => event.preventDefault()}
            >
                <svg class="file-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                </svg>
                <div class="file-info">
                    <span class="file-name">${filename}</span>
                    <span class="file-meta-row">
                        ${sizeStr && html`<span class="file-size">${sizeStr}</span>`}
                        ${contentType && html`<span class="file-size">${contentType}</span>`}
                    </span>
                </div>
                <svg class="download-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
            </a>
            ${canPreview && html`
                <button
                    class="file-attachment-preview"
                    type="button"
                    onClick=${(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onPreview?.({ mediaId, info });
                    }}
                >
                    Preview
                </button>
            `}
        </div>
    `;
}

const PROTECTED_RECOVERY_CONTROL_INTENT = 'protected_recovery_continuation';
const PROTECTED_RECOVERY_CONTROL_LABEL = 'Recovery resumed with execution tools';
const PROTECTED_RECOVERY_REASONS = new Set([
    'post_compaction_tools_required',
    'tools_required',
    'compaction_failed',
    'recovery_budget_exhausted',
    'unresolved_tool_execution',
    'continuation_generation_exhausted',
    'provider_retry_exhausted',
]);
const PROTECTED_RECOVERY_TYPED_KEYS = ['reason', 'compaction', 'tools_required', 'retryable', 'recovery_attempts'];
const PRIMARY_FAILURE_KEYS = [
    'primary_failure_category',
    'primary_failure_detail',
    'primary_failure_elapsed_ms',
    'primary_failure_execution_tools',
    'primary_failure_had_partial_output',
    'primary_failure_had_tool_activity',
    'primary_failure_tool_executions',
];

function hasValidPrimaryFailureFields(block) {
    const count = PRIMARY_FAILURE_KEYS.filter(key => Object.prototype.hasOwnProperty.call(block, key)).length;
    if (count === 0) return true;
    return count === PRIMARY_FAILURE_KEYS.length
        && block.primary_failure_category === 'timeout'
        && typeof block.primary_failure_detail === 'string'
        && block.primary_failure_detail.trim().length <= 500
        && /^Timed out after \d+s\.$/.test(block.primary_failure_detail.trim())
        && Number.isInteger(block.primary_failure_elapsed_ms)
        && block.primary_failure_elapsed_ms >= 0
        && block.primary_failure_elapsed_ms <= 30 * 24 * 60 * 60 * 1000
        && typeof block.primary_failure_execution_tools === 'boolean'
        && typeof block.primary_failure_had_partial_output === 'boolean'
        && typeof block.primary_failure_had_tool_activity === 'boolean'
        && Number.isInteger(block.primary_failure_tool_executions)
        && block.primary_failure_tool_executions >= 0
        && block.primary_failure_tool_executions <= 1000000;
}

function hasValidProtectedRecoveryHandoffFields(block) {
    const hasTypedFields = PROTECTED_RECOVERY_TYPED_KEYS.some(key => Object.prototype.hasOwnProperty.call(block, key));
    if (!hasTypedFields) return true;
    const valid = PROTECTED_RECOVERY_REASONS.has(block.reason)
        && ['not_attempted', 'succeeded', 'failed'].includes(block.compaction)
        && typeof block.tools_required === 'boolean'
        && typeof block.retryable === 'boolean'
        && Number.isInteger(block.recovery_attempts)
        && block.recovery_attempts >= 0;
    if (!valid || !hasValidPrimaryFailureFields(block)) return false;
    if (block.reason === 'post_compaction_tools_required') return block.compaction === 'succeeded' && block.tools_required;
    if (block.reason === 'compaction_failed') return block.compaction === 'failed';
    if (block.reason === 'tools_required' || block.reason === 'unresolved_tool_execution') return block.tools_required;
    return true;
}

export function getProtectedRecoveryControlIntent(contentBlocks) {
    const block = Array.isArray(contentBlocks)
        ? contentBlocks.find((candidate) => (
            candidate
            && typeof candidate === 'object'
            && candidate.type === 'control_intent'
            && candidate.intent === PROTECTED_RECOVERY_CONTROL_INTENT
            && candidate.schema_version === 1
            && typeof candidate.source_message_id === 'string'
            && candidate.source_message_id.trim().length > 0
            && Number.isInteger(candidate.source_row_id)
            && Number(candidate.source_row_id) > 0
            && Number.isInteger(candidate.thread_id)
            && Number(candidate.thread_id) > 0
            && Number.isInteger(candidate.handoff_depth ?? 1)
            && Number(candidate.handoff_depth ?? 1) > 0
            && hasValidProtectedRecoveryHandoffFields(candidate)
        ))
        : null;
    if (block) {
        return {
            label: PROTECTED_RECOVERY_CONTROL_LABEL,
            sourceMessageId: typeof block.source_message_id === 'string' ? block.source_message_id : '',
            sourceRowId: Number.isFinite(Number(block.source_row_id)) ? Number(block.source_row_id) : null,
        };
    }
    return null;
}

export function extractRecoveryMarkerBlocks(contentBlocks) {
    if (!Array.isArray(contentBlocks)) return [];
    return contentBlocks.filter((block) => block && typeof block === 'object' && block.type === 'recovery_marker' && block.recovered);
}

export function extractTimeoutMarkerBlocks(contentBlocks) {
    if (!Array.isArray(contentBlocks)) return [];
    return contentBlocks.filter((block) => block && typeof block === 'object' && block.type === 'timeout_marker' && (block.timed_out ?? true));
}

export function extractOutcomeMarkerBlocks(contentBlocks) {
    if (!Array.isArray(contentBlocks)) return [];
    return contentBlocks.filter((block) => block && typeof block === 'object' && block.type === 'turn_outcome_marker');
}

export function extractAgentTimingBlock(contentBlocks) {
    if (!Array.isArray(contentBlocks)) return null;
    return contentBlocks.find((block) => block && typeof block === 'object' && block.type === 'agent_timing') || null;
}

export function formatAgentReplyDuration(durationMs) {
    if (durationMs === null || durationMs === undefined || durationMs === '') return null;
    const ms = Number(durationMs);
    if (!Number.isFinite(ms) || ms < 0) return null;
    if (ms < 1000) return `${Math.round(ms)}ms`;
    const totalSeconds = ms / 1000;
    if (totalSeconds < 60) {
        return totalSeconds < 10 ? `${totalSeconds.toFixed(1)}s` : `${Math.round(totalSeconds)}s`;
    }
    const wholeSeconds = Math.round(totalSeconds);
    const minutes = Math.floor(wholeSeconds / 60);
    const seconds = wholeSeconds % 60;
    if (minutes < 60) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${String(remainingMinutes).padStart(2, '0')}m`;
}

function readUsageNumber(usage, key) {
    const value = Number(usage?.[key]);
    return Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
}

export function formatAgentTokenStats(usage) {
    if (!usage || typeof usage !== 'object') return null;
    const input = readUsageNumber(usage, 'input_tokens');
    const output = readUsageNumber(usage, 'output_tokens');
    const reasoning = readUsageNumber(usage, 'reasoning_tokens');
    const cacheRead = readUsageNumber(usage, 'cache_read_tokens');
    const cacheWrite = readUsageNumber(usage, 'cache_write_tokens');
    const total = readUsageNumber(usage, 'total_tokens') || input + output + cacheRead + cacheWrite;
    if (!total && !input && !output && !reasoning && !cacheRead && !cacheWrite) return null;
    const totals = [`Tokens: ${formatCount(total)} total`];
    if (input) totals.push(`Input: ${formatCount(input)}`);
    if (output) totals.push(`Output: ${formatCount(output)}`);
    const details = [];
    if (reasoning) details.push(`Reasoning: ${formatCount(reasoning)}`);
    if (cacheRead) details.push(`Cache read: ${formatCount(cacheRead)}`);
    if (cacheWrite) details.push(`Cache write: ${formatCount(cacheWrite)}`);
    return [totals.join(' · '), details.join(' · ')].filter(Boolean).join('\n');
}

function readUsageCost(usage, key) {
    const value = usage?.[key];
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

function formatAgentCost(usage) {
    if (!usage || typeof usage !== 'object') return null;
    const providerCost = readUsageCost(usage, 'provider_cost_total');
    const effectiveCost = readUsageCost(usage, 'cost_total');
    const provenance = usage.cost_provenance;
    const cost = provenance === 'provider_reported' ? providerCost ?? effectiveCost : effectiveCost;
    if (cost === null) return null;
    const formatted = `$${cost.toFixed(cost >= 0.01 ? 2 : 4)}`;
    if (provenance === 'provider_reported') return `Provider-reported cost: ${formatted}`;
    if (provenance === 'catalogue_estimate') return `Catalogue cost estimate: ~${formatted}`;
    return null;
}

export function buildPostTimeTooltip(post, timingBlock = extractAgentTimingBlock(post?.data?.content_blocks)) {
    const sent = formatTimestamp(post?.timestamp);
    const parts = [`Sent ${sent}`];
    const duration = formatAgentReplyDuration(timingBlock?.duration_ms);
    if (duration) parts[0] += ` · Agent reply took ${duration}`;
    const tokenStats = formatAgentTokenStats(timingBlock?.usage);
    if (tokenStats) parts.push(tokenStats);
    const cost = formatAgentCost(timingBlock?.usage);
    if (cost) parts.push(cost);
    const startedAt = typeof timingBlock?.started_at === 'string' && timingBlock.started_at.trim()
        ? formatTimestamp(timingBlock.started_at)
        : '';
    if (startedAt) parts.push(`Started ${startedAt}`);
    return parts.join('\n');
}

export function extractPeerMessageBlocks(contentBlocks) {
    if (!Array.isArray(contentBlocks)) return [];
    return contentBlocks.filter((block) => block && typeof block === 'object' && block.type === 'peer_message');
}

function fallbackPeerAgentHandle(chatJid) {
    return String(chatJid || '')
        .split(/[:/]/)
        .filter(Boolean)
        .pop() || 'agent';
}

export function getPeerMessageMeta(contentBlocks) {
    const block = extractPeerMessageBlocks(contentBlocks)[0] || null;
    if (!block) return null;
    const sourceChatJid = typeof block.source_chat_jid === 'string' ? block.source_chat_jid.trim() : '';
    const sourceAgentName = typeof block.source_agent_name === 'string' && block.source_agent_name.trim()
        ? block.source_agent_name.trim()
        : fallbackPeerAgentHandle(sourceChatJid);
    const sourceAgentDisplayName = typeof block.source_agent_display_name === 'string' && block.source_agent_display_name.trim()
        ? block.source_agent_display_name.trim()
        : sourceAgentName;
    const targetChatJid = typeof block.target_chat_jid === 'string' ? block.target_chat_jid.trim() : '';
    const targetAgentName = typeof block.target_agent_name === 'string' ? block.target_agent_name.trim() : '';
    const targetAgentDisplayName = typeof block.target_agent_display_name === 'string' ? block.target_agent_display_name.trim() : '';
    const body = typeof block.body === 'string' ? block.body : '';
    return {
        block,
        sourceChatJid,
        sourceAgentName,
        sourceAgentDisplayName,
        targetChatJid,
        targetAgentName,
        targetAgentDisplayName,
        body,
    };
}

export function getPeerMessageDisplayContent(content, contentBlocks) {
    const peer = getPeerMessageMeta(contentBlocks);
    const original = typeof content === 'string' ? content : '';
    if (!peer) return original;
    if (peer.body && peer.body.trim()) return peer.body;
    const sourceAgentPattern = peer.sourceAgentName
        ? peer.sourceAgentName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        : '[^\\s>]+';
    const sourceChatPattern = peer.sourceChatJid
        ? peer.sourceChatJid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        : '[^>\\n]+';
    const stripped = original.replace(new RegExp(`^from:\\s+@${sourceAgentPattern}\\s+<jid:${sourceChatPattern}>\\s*\\n\\n`, 'i'), '');
    return stripped || original;
}

export function getSelfContinuationMeta(contentBlocks) {
    if (!Array.isArray(contentBlocks)) return null;
    const block = contentBlocks.find((candidate) => (
        candidate
        && typeof candidate === 'object'
        && candidate.type === 'self_continuation'
        && candidate.source === 'exit_process'
    ));
    if (!block) return null;
    const restartId = typeof block.restart_id === 'string' ? block.restart_id.trim() : '';
    if (!restartId) return null;
    const handoff = getRestartHandoffMeta(contentBlocks);
    if (!handoff || handoff.phase !== 'resume' || handoff.restartId !== restartId) return null;
    return { block, restartId };
}

export function getRestartHandoffMeta(contentBlocks) {
    if (!Array.isArray(contentBlocks)) return null;
    const block = contentBlocks.find((candidate) => (
        candidate
        && typeof candidate === 'object'
        && candidate.type === 'restart_handoff'
        && candidate.source === 'exit_process'
    ));
    if (!block) return null;
    const restartId = typeof block.restart_id === 'string' ? block.restart_id.trim() : '';
    const phase = typeof block.phase === 'string' ? block.phase.trim() : '';
    if (!restartId || !['notice', 'completion', 'resume'].includes(phase)) return null;
    const reason = typeof block.reason === 'string' ? block.reason.trim() : '';
    return { block, restartId, phase, reason };
}

const RECOVERY_CLASSIFIER_LABELS = {
    context_recover: 'context limit exceeded',
    rate_limit: 'rate limit hit',
    api_error: 'API error',
    timeout: 'request timeout',
    overloaded: 'service overloaded',
    connection: 'connection error',
};

export function formatRecoveryChipTooltip(marker) {
    const attempts = Number(marker?.attempts_used || 0);
    const classifier = String(marker?.classifier || '').trim();
    const reason = RECOVERY_CLASSIFIER_LABELS[classifier] || (classifier ? classifier.replace(/_/g, ' ') : '');
    const parts = ['Recovered automatically'];
    if (attempts > 1) parts[0] = `Recovered after ${attempts} attempts`;
    if (reason) parts.push(reason);
    return parts.join(' — ');
}

export function formatTimeoutChipTooltip(marker) {
    const action = typeof marker?.tool_action_summary === 'string' ? marker.tool_action_summary.trim() : '';
    const recoveredDraft = marker?.draft_recovered ? ' Showing recovered draft.' : '';
    return action
        ? `Turn timed out — ${action}${recoveredDraft}`
        : `Turn timed out before the model finished responding${recoveredDraft}`;
}

export function formatOutcomeChipTooltip(marker) {
    const title = typeof marker?.title === 'string' ? marker.title.trim() : '';
    const detail = typeof marker?.detail === 'string' ? marker.detail.trim() : '';
    const action = typeof marker?.tool_action_summary === 'string' ? marker.tool_action_summary.trim() : '';
    const nextAction = typeof marker?.next_action === 'string' ? marker.next_action.trim() : '';
    const recoveredDraft = marker?.draft_recovered ? ' Showing recovered draft.' : '';
    const parts = [title, detail];
    if (action) parts.push(`Last action: ${action}`);
    if (nextAction) parts.push(`Next: ${nextAction}`);
    return parts.filter(Boolean).join(' — ') + recoveredDraft;
}

function OutcomePill({ marker }) {
    const [expanded, setExpanded] = useState(false);
    const toggle = useCallback((e) => { e.stopPropagation(); setExpanded(v => !v); }, []);

    const title = typeof marker?.title === 'string' ? marker.title.trim() : '';
    const detail = typeof marker?.detail === 'string' ? marker.detail.trim() : '';
    const action = typeof marker?.tool_action_summary === 'string' ? marker.tool_action_summary.trim() : '';
    const nextAction = typeof marker?.next_action === 'string' ? marker.next_action.trim() : '';
    const reason = typeof marker?.reason === 'string' ? marker.reason.replaceAll('_', ' ') : '';
    const compaction = marker?.compaction === 'succeeded' || marker?.compaction === 'failed'
        ? `compaction ${marker.compaction}`
        : '';
    const attempts = Number.isInteger(marker?.recovery_attempts)
        ? `${marker.recovery_attempts} recovery attempt${marker.recovery_attempts === 1 ? '' : 's'}`
        : '';
    const toolExecutions = Number.isInteger(marker?.primary_failure_tool_executions)
        ? `${marker.primary_failure_tool_executions} tool execution${marker.primary_failure_tool_executions === 1 ? '' : 's'}`
        : '';
    const typedSummary = [
        reason,
        compaction,
        marker?.tools_required === true ? 'tools required' : '',
        attempts,
        marker?.primary_failure_execution_tools === true ? 'execution tools enabled' : '',
        marker?.primary_failure_had_partial_output === true ? 'partial output' : '',
        marker?.primary_failure_had_tool_activity === true ? 'tool activity' : '',
        toolExecutions,
    ].filter(Boolean).join(' · ');
    const draftRecovered = marker?.draft_recovered;
    const severity = String(marker?.severity || 'warning');
    const label = action || title || String(marker?.label || marker?.kind || 'issue');

    const hasDetail = Boolean(detail || nextAction || typedSummary || (title && action));

    return html`
        <div class=${`post-outcome-pill post-outcome-pill-${severity}`}>
            <div class="post-outcome-pill-header" onClick=${hasDetail ? toggle : undefined}>
                ${hasDetail && html`
                    <span class="post-outcome-pill-toggle" aria-hidden="true">${renderDisclosureTriangle(expanded ? 'down' : 'right')}</span>
                `}
                <span class="post-outcome-pill-label">${label}</span>
                ${draftRecovered && html`<span class="post-outcome-pill-badge">draft recovered</span>`}
            </div>
            ${expanded && hasDetail && html`
                <div class="post-outcome-pill-detail">
                    ${title && html`<div><strong>${title}</strong></div>`}
                    ${detail && detail !== title && html`<div>${detail}</div>`}
                    ${nextAction && html`<div><strong>Next:</strong> ${nextAction}</div>`}
                    ${typedSummary && html`<div class="post-outcome-pill-meta">${typedSummary}</div>`}
                </div>
            `}
        </div>
    `;
}

function ThinkingVisibilityPill({ messageId, chatJid, lines, durationMs, loadThinking }) {
    const [expanded, setExpanded] = useState(false);
    const [text, setText] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [copyState, setCopyState] = useState('idle');
    const [fetchedDuration, setFetchedDuration] = useState(null);
    const copyResetTimerRef = useRef(null);
    const mountedRef = useRef(true);
    const rootRef = useRef(null);
    const panelRef = useRef(null);
    const anchorRef = useRef(null);

    // Keep the header visually fixed while the panel expands/collapses. No-op in
    // browsers with native scroll anchoring (Chrome/Edge/Firefox); active only in
    // WebKit (incl. all iOS/iPadOS browsers), which lacks `overflow-anchor`. The
    // panel is always mounted (see render) so the ResizeObserver inside the anchor
    // sees both expand and collapse. useLayoutEffect so the anchor is wired before
    // any user toggle can occur.
    useLayoutEffect(() => {
        const scroller = rootRef.current?.closest('.timeline');
        if (!scroller) return undefined;
        const anchor = attachHeaderAnchor(scroller, panelRef.current);
        anchorRef.current = anchor;
        return () => {
            anchor.dispose();
            anchorRef.current = null;
        };
    }, []);

    // Apply the compensation synchronously after each toggle re-render (before
    // paint), covering the synchronous (cached-content) expand in addition to the
    // ResizeObserver which covers async content growth. No-op except in Safari.
    useLayoutEffect(() => {
        anchorRef.current?.compensate();
    }, [expanded, loading, text]);

    const toggle = useCallback((e) => {
        e.stopPropagation();
        // Snapshot the scroll/panel state before the height change so the anchor
        // can hold the header fixed (Safari only). Must run before setExpanded.
        anchorRef.current?.mark();
        setExpanded(v => {
            const next = !v;
            if (next && !text && !loading && !error) {
                setLoading(true);
                const request = typeof loadThinking === 'function'
                    ? Promise.resolve().then(() => loadThinking(messageId, chatJid || ''))
                    : fetch(`/agent/thinking?message_id=${encodeURIComponent(messageId)}&chat_jid=${encodeURIComponent(chatJid || '')}`)
                        .then(r => {
                            if (!r.ok) throw new Error(`HTTP ${r.status}`);
                            return r.json();
                        });
                request.then(d => {
                        if (!mountedRef.current) return;
                        const nextText = typeof d?.text === 'string' && d.text.length <= 100_000 ? d.text : '';
                        const nextDuration = Number.isFinite(d?.duration_ms) && d.duration_ms >= 0 ? d.duration_ms : null;
                        if (nextText) setText(nextText);
                        else setError(true);
                        if (nextDuration !== null) setFetchedDuration(nextDuration);
                    })
                    .catch(err => {
                        if (!mountedRef.current) return;
                        console.warn('[post] Failed to load thinking content:', err);
                        setError(true);
                    })
                    .finally(() => { if (mountedRef.current) setLoading(false); });
            }
            return next;
        });
    }, [text, loading, error, messageId, chatJid, loadThinking]);

    const scheduleCopyStateReset = useCallback(() => {
        if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current);
        copyResetTimerRef.current = setTimeout(() => {
            copyResetTimerRef.current = null;
            setCopyState('idle');
        }, 2000);
    }, []);

    const copyThinking = useCallback((e) => {
        e.stopPropagation();
        if (!text) return;
        // Route through the shared best-effort copy (execCommand / clipboard /
        // textarea fallback) so it works in insecure (plain-http) contexts and
        // older mobile browsers where navigator.clipboard is undefined.
        copyTextToClipboard(text).then((ok) => {
            if (!mountedRef.current) return;
            setCopyState(ok ? 'success' : 'error');
            scheduleCopyStateReset();
        }).catch(err => {
            console.warn('[post] Failed to copy thinking content:', err);
            if (!mountedRef.current) return;
            setCopyState('error');
            scheduleCopyStateReset();
        });
    }, [text, scheduleCopyStateReset]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current);
        };
    }, []);

    // Render thinking text via the thinking-specific renderer so the persisted
    // pill displays identically to the live thought panel (status.ts /
    // btw-panel.ts both use renderThinkingMarkdown). This matters because:
    //   - renderThinkingMarkdown escapes all <> first then restores only
    //     allowed tags (stricter than generic sanitiseHtml)
    //   - it disables LaTeX math rendering so shell content like $PATH does
    //     not get mangled as math expressions
    //   - it skips hashtag linkification (thinking is internal reasoning,
    //     not a chat message)
    const renderedHtml = useMemo(() => {
        if (!text) return null;
        try { return { __html: renderThinkingMarkdown(text) }; }
        catch { return null; }
    }, [text]);

    const ms = fetchedDuration || durationMs || 0;
    const durationLabel = ms > 0
        ? `Thought for ${ms < 1000 ? Math.round(ms) + 'ms' : ms < 60000 ? Math.round(ms / 1000) + 's' : Math.floor(ms / 60000) + 'm ' + (Math.round(ms / 1000) % 60) + 's'}`
        : `Thinking · ${lines} line${lines !== 1 ? 's' : ''}`;

    // Stable detail id so the header button can declare aria-controls — needed
    // for screen readers to announce the relationship between the toggle and
    // the expandable region.
    const detailId = `post-thinking-visibility-detail-${messageId}`;

    return html`
        <div class="post-thinking-visibility" data-expanded=${expanded ? 'true' : 'false'} ref=${rootRef}>
            <div class="post-thinking-visibility-controls">
                <button
                    type="button"
                    class="post-thinking-visibility-header"
                    onClick=${toggle}
                    aria-expanded=${expanded ? 'true' : 'false'}
                    aria-controls=${detailId}
                    aria-label=${`${expanded ? 'Collapse' : 'Expand'} reasoning trace, ${durationLabel}`}
                >
                    <span class="post-thinking-visibility-toggle" aria-hidden="true">
                        ${renderDisclosureTriangle(expanded ? 'down' : 'right')}
                    </span>
                    <span class="post-thinking-visibility-label">${durationLabel}</span>
                </button>
                ${expanded && text && html`
                    <button
                        type="button"
                        class=${`post-action-btn post-copy-btn post-thinking-visibility-copy${copyState === 'success' ? ' is-success' : copyState === 'error' ? ' is-error' : ''}`}
                        onClick=${copyThinking}
                        aria-label=${copyState === 'success' ? 'Copied' : copyState === 'error' ? 'Copy failed' : 'Copy reasoning trace'}
                        title=${copyState === 'success' ? 'Copied' : copyState === 'error' ? 'Copy failed' : 'Copy reasoning trace'}
                    >
                        ${copyState === 'success'
                            ? html`<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M20 6L9 17l-5-5"></path></svg>`
                            : copyState === 'error'
                                ? html`<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9"></circle><path d="M9 9l6 6M15 9l-6 6"></path></svg>`
                                : html`<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="9" y="9" width="10" height="10" rx="2"></rect><path d="M7 15H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1"></path></svg>`}
                    </button>
                `}
                <span class="sr-only" role="status" aria-live="polite">
                    ${copyState === 'success' ? 'Copied reasoning trace' : copyState === 'error' ? 'Copy failed' : ''}
                </span>
                <span class="sr-only" role="status" aria-live="polite">
                    ${expanded ? (loading ? 'Loading reasoning trace' : error ? 'Could not load reasoning trace' : text ? 'Reasoning trace loaded' : '') : ''}
                </span>
            </div>
            <div
                id=${detailId}
                class="post-thinking-visibility-detail"
                ref=${panelRef}
            >
                ${expanded ? html`
                    ${loading ? html`<span class="post-thinking-visibility-loading">Loading reasoning trace…</span>` : null}
                    ${error ? html`<span class="post-thinking-visibility-error">Could not load reasoning trace.</span>` : null}
                    ${renderedHtml ? html`<div dangerouslySetInnerHTML=${renderedHtml} />` : (!loading && !error && text ? text : null)}
                ` : null}
            </div>
        </div>
    `;
}

function AttachmentPill({ attachment, onPreview, allowActions = true, loadInfo = getMediaInfo }) {
    const mediaId = Number(attachment?.id);
    const [info, setInfo] = useState(null);

    useEffect(() => {
        if (!Number.isFinite(mediaId)) return undefined;
        let cancelled = false;
        Promise.resolve().then(() => loadInfo(mediaId)).then((value) => {
            if (!cancelled) setInfo(value);
        }).catch((error) => {
            if (!cancelled) console.warn('[post] Failed to load attachment metadata for attachment pill:', mediaId, error);
        });
        return () => { cancelled = true; };
    }, [loadInfo, mediaId]);

    const attachmentLabel = typeof attachment?.label === 'string' && attachment.label.trim()
        ? attachment.label.trim()
        : `attachment-${attachment.id}`;
    const filename = typeof info?.filename === 'string' && info.filename.trim() ? info.filename.trim() : attachmentLabel;
    const contentType = typeof info?.content_type === 'string' ? info.content_type : '';
    const downloadHref = allowActions && Number.isFinite(mediaId) ? getMediaUrl(mediaId) : null;
    const previewKind = getAttachmentPreviewKind(contentType, filename);
    const previewLabel = getAttachmentPreviewLabel(previewKind);
    const canPreview = allowActions && previewKind !== 'unsupported';

    return html`
        <span class="attachment-pill" title=${filename}>
            ${downloadHref
                ? html`
                    <a href=${downloadHref} download=${filename} class="attachment-pill-main" onClick=${(e) => e.stopPropagation()}>
                        <${FilePill}
                            prefix="post"
                            label=${attachmentLabel}
                            title=${filename}
                        />
                    </a>
                `
                : html`
                    <${FilePill}
                        prefix="post"
                        label=${attachmentLabel}
                        title=${filename}
                    />
                `}
            ${Number.isFinite(mediaId) && info && canPreview && html`
                <button
                    class="attachment-pill-preview"
                    type="button"
                    title=${previewLabel}
                    onClick=${(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onPreview?.({ mediaId, info });
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"/>
                        <circle cx="12" cy="12" r="3"/>
                    </svg>
                </button>
            `}
        </span>
    `;
}

/**
 * Render annotations (audience/priority/lastModified)
 */
function AnnotationsBadge({ annotations }) {
    if (!annotations || typeof annotations !== 'object' || Array.isArray(annotations)) return null;
    const audience = Array.isArray(annotations.audience)
        ? annotations.audience
            .filter((value) => typeof value === 'string' && value.trim())
            .map((value) => value.trim().slice(0, 256))
            .slice(0, 20)
        : [];
    const priority = typeof annotations.priority === 'number' && Number.isFinite(annotations.priority)
        ? annotations.priority
        : null;
    const lastModified = typeof annotations.lastModified === 'string' ? annotations.lastModified : '';
    const formattedLastModified = lastModified && Number.isFinite(Date.parse(lastModified))
        ? formatTimestamp(lastModified)
        : null;
    return html`
        <div class="content-annotations">
            ${audience && audience.length > 0 && html`
                <span class="content-annotation">Audience: ${audience.join(', ')}</span>
            `}
            ${priority !== null && html`
                <span class="content-annotation">Priority: ${priority}</span>
            `}
            ${formattedLastModified && html`
                <span class="content-annotation">Updated: ${formattedLastModified}</span>
            `}
        </div>
    `;
}

/**
 * Resource link block (MCP/ACP)
 */
function ResourceLinkBlock({ block, allowOpen = true }) {
    const uri = typeof block?.uri === 'string' ? block.uri : '';
    const name = [block?.title, block?.name, uri].find((value) => typeof value === 'string' && value.trim()) || 'Resource';
    const description = typeof block?.description === 'string' ? block.description : '';
    const sizeStr = typeof block?.size === 'number' && Number.isFinite(block.size) && block.size > 0 ? formatFileSize(block.size) : '';
    const mimeType = typeof block?.mime_type === 'string' ? block.mime_type : '';
    const icon = getMimeIcon(mimeType);
    const safeUrl = sanitizeUrl(uri);
    const href = allowOpen ? safeUrl : null;
    return html`
        <a
            href=${href || undefined}
            class="resource-link"
            target=${href ? "_blank" : undefined}
            rel=${href ? "noopener noreferrer" : undefined}
            aria-disabled=${href ? undefined : 'true'}
            onClick=${(e) => { e.stopPropagation(); if (!href) e.preventDefault(); }}>
            <div class="resource-link-main">
                <div class="resource-link-header">
                    <span class="resource-link-icon-inline">${icon}</span>
                    <div class="resource-link-title">${name}</div>
                </div>
                ${description && html`<div class="resource-link-description">${description}</div>`}
                <div class="resource-link-meta">
                    ${mimeType && html`<span>${mimeType}</span>`}
                    ${sizeStr && html`<span>${sizeStr}</span>`}
                </div>
            </div>
            <div class="resource-link-icon">↗</div>
        </a>
    `;
}

/**
 * Embedded resource block (MCP/ACP)
 */
// Expansion reveals only already-persisted text in the page. Blob creation and
// download remain a separate capability controlled by allowDownload.
function ResourceBlock({ block, allowDownload = true }) {
    const [open, setOpen] = useState(false);
    const title = typeof block?.uri === 'string' && block.uri.trim() ? block.uri : 'Embedded resource';
    const contentText = typeof block?.text === 'string' ? block.text : '';
    const blobData = typeof block?.data === 'string' ? block.data : '';
    const hasBlob = Boolean(blobData);
    const mimeType = typeof block?.mime_type === 'string' ? block.mime_type : '';
    return html`
        <div class="resource-embed">
            <button class="resource-embed-toggle" onClick=${(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); }}>
                ${renderDisclosureTriangle(open ? 'down' : 'right')} ${title}
            </button>
            ${open && html`
                ${contentText && html`<pre class="resource-embed-content">${contentText}</pre>`}
                ${hasBlob && html`
                    <div class="resource-embed-blob">
                        <span class="resource-embed-blob-label">Embedded blob</span>
                        ${mimeType && html`<span class="resource-embed-blob-meta">${mimeType}</span>`}
                        <button class="resource-embed-blob-btn" disabled=${!allowDownload} onClick=${(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!allowDownload) return;
                            const blob = new Blob([Uint8Array.from(atob(blobData), c => c.charCodeAt(0))], { type: mimeType || 'application/octet-stream' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = title.split('/').pop() || 'resource';
                            a.click();
                            URL.revokeObjectURL(url);
                        }}>Download</button>
                    </div>
                `}
            `}
        </div>
    `;
}

function GeneratedWidgetLaunch({ block, post, onOpenWidget, allowOpen = true }) {
    if (!block) return null;

    const payload = buildGeneratedWidgetPayload(block, post);
    const supportsRender = canRenderGeneratedWidget(block);
    const canOpen = allowOpen && supportsRender && typeof onOpenWidget === 'function';
    const kind = payload?.artifact?.kind || block?.artifact?.kind || block?.kind || null;
    const title = [payload?.title, block.title, block.name]
        .find((value) => typeof value === 'string' && value.trim()) || 'Generated widget';
    const description = [payload?.description, block.description, block.subtitle]
        .find((value) => typeof value === 'string' && value.trim()) || '';
    const openLabel = typeof block.open_label === 'string' && block.open_label.trim()
        ? block.open_label.trim()
        : 'Open widget';
    const autoOpened = useRef(false);
    const launchWidget = (e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        if (!canOpen || !payload) return;
        onOpenWidget?.(payload);
    };

    useEffect(() => {
        if (!block?.auto_open || !payload || !canOpen || autoOpened.current) return;
        // Only auto-open for messages posted in the last 10 seconds to avoid
        // re-opening stale widgets on page refresh or timeline scroll.
        const postTime = post?.timestamp ? new Date(post.timestamp).getTime() : 0;
        if (postTime && (Date.now() - postTime) > 10_000) return;
        // Prevent re-open across page refreshes using sessionStorage
        const key = `widget_opened_${block.widget_id || post?.id || ''}`;
        if (readSessionStorageFlagBestEffort(sessionStorage, key)) return;
        autoOpened.current = true;
        writeSessionStorageFlagBestEffort(sessionStorage, key, '1');
        onOpenWidget?.(payload);
    }, [block?.auto_open, payload, canOpen]);

    return html`
        <div class="generated-widget-launch" onClick=${(e) => e.stopPropagation()}>
            <div class="generated-widget-launch-header">
                <div class="generated-widget-launch-eyebrow">Generated widget${kind ? ` • ${String(kind).toUpperCase()}` : ''}</div>
                <div class="generated-widget-launch-title">${title}</div>
            </div>
            ${description && html`<div class="generated-widget-launch-description">${description}</div>`}
            <div class="generated-widget-launch-actions">
                <button
                    class="generated-widget-launch-btn"
                    type="button"
                    disabled=${!canOpen}
                    onClick=${launchWidget}
                    title=${canOpen ? 'Open widget in a floating pane with a zen-mode toggle' : supportsRender ? 'Widget opening is unavailable here' : 'Unsupported widget artifact'}
                >
                    ${openLabel}
                </button>
                <span class="generated-widget-launch-note">
                    ${canOpen
                        ? 'Opens in a dismissible floating pane with a zen-mode toggle.'
                        : supportsRender ? 'This widget is shown read-only.' : 'This widget artifact is missing or unsupported.'}
                </span>
            </div>
        </div>
    `;
}

function getMimeIcon(mimeType) {
    if (!mimeType) return '📎';
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.startsWith('audio/')) return '🎵';
    if (mimeType.startsWith('video/')) return '🎬';
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('zip') || mimeType.includes('gzip')) return '🗜️';
    if (mimeType.startsWith('text/')) return '📄';
    return '📎';
}

/**
 * Link preview component - card with image background
 */
export function buildLinkPreviewBackgroundStyle(imageUrl) {
    const safeImage = sanitizeUrl(imageUrl, { allowDataImage: true });
    return safeImage
        ? { backgroundImage: `url("${safeImage}")` }
        : undefined;
}

export function rewriteOwnedMediaUrl(value) {
    const raw = typeof value === 'string' ? value.trim() : '';
    return /^\/media\/[1-9]\d*(?:\/thumbnail)?$/.test(raw) ? raw : '';
}

function LinkPreview({ preview, rewriteImageSrc }) {
    const url = typeof preview?.url === 'string' ? preview.url : '';
    const image = typeof preview?.image === 'string' ? preview.image : '';
    const title = typeof preview?.title === 'string' ? preview.title : '';
    const description = typeof preview?.description === 'string' ? preview.description : '';
    const suppliedSiteName = typeof preview?.site_name === 'string' ? preview.site_name : '';
    const safeUrl = sanitizeUrl(url);
    const rewrittenImage = typeof rewriteImageSrc === 'function' ? rewriteImageSrc(image) : image;
    const bgStyle = buildLinkPreviewBackgroundStyle(rewrittenImage);
    const siteName = resolveLinkPreviewSiteName(suppliedSiteName, safeUrl);

    return html`
        <a
            href=${safeUrl || '#'}
            class="link-preview ${bgStyle ? 'has-image' : ''}"
            target=${safeUrl ? "_blank" : undefined}
            rel=${safeUrl ? "noopener noreferrer" : undefined}
            onClick=${(e) => e.stopPropagation()}
            style=${bgStyle}>
            <div class="link-preview-overlay">
                <div class="link-preview-site">${siteName || ''}</div>
                <div class="link-preview-title">${title}</div>
                ${description && html`
                    <div class="link-preview-description">${description}</div>
                `}
            </div>
        </a>
    `;
}

/**
 * Preserve message text exactly as-authored, even when link previews exist.
 *
 * Regression note: we intentionally do not strip URLs from content when a
 * preview card is shown. Users expect their original text to remain visible.
 */
export function getDisplayContent(content, _linkPreviews) {
    return typeof content === 'string' ? content : '';
}

const CODE_COPY_RESET_MS = 1800;
const LARGE_CODE_BLOCK_COLLAPSE_LINES = 40;
const LARGE_CODE_BLOCK_COLLAPSE_BYTES = 24 * 1024;
const LARGE_CODE_BLOCK_PREVIEW_LINES = 16;
const COPY_ICON_SVG = `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <rect x="9" y="9" width="10" height="10" rx="2"></rect>
        <path d="M7 15H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1"></path>
    </svg>`;
const COPY_SUCCESS_SVG = `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M20 6L9 17l-5-5"></path>
    </svg>`;
const COPY_ERROR_SVG = `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <circle cx="12" cy="12" r="9"></circle>
        <path d="M9 9l6 6M15 9l-6 6"></path>
    </svg>`;
const CLIPBOARD_STYLE = `
<style>
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: #1a1a1a;
  }
  h1 { font-size: 1.6em; font-weight: 700; margin: 0.6em 0 0.4em; }
  h2 { font-size: 1.35em; font-weight: 700; margin: 0.6em 0 0.4em; }
  h3 { font-size: 1.15em; font-weight: 700; margin: 0.5em 0 0.3em; }
  h4, h5, h6 { font-size: 1em; font-weight: 700; margin: 0.5em 0 0.3em; }
  p { margin: 0.5em 0; }
  pre {
    background: #f6f8fa;
    border: 1px solid #d0d7de;
    border-radius: 6px;
    padding: 12px 16px;
    overflow-x: auto;
    margin: 0.5em 0;
  }
  code {
    font-family: "Fira Code", "Cascadia Code", Consolas, "Courier New", monospace;
    font-size: 0.9em;
  }
  pre code { background: none; padding: 0; border: none; }
  :not(pre) > code { background: #f0f2f5; padding: 2px 5px; border-radius: 3px; }
  blockquote { border-left: 3px solid #d0d7de; margin: 0.5em 0; padding-left: 12px; color: #57606a; }
  .admonition { border-left: 4px solid; border-radius: 4px; margin: 0.5em 0; padding: 8px 12px; }
  .admonition p { margin: 0.25em 0; }
  .admonition-title { font-weight: 600; margin-bottom: 0.25em !important; }
  .admonition-note { border-color: #4493f8; background: rgba(68,147,248,0.08); }
  .admonition-note .admonition-title { color: #4493f8; }
  .admonition-tip { border-color: #3fb950; background: rgba(63,185,80,0.08); }
  .admonition-tip .admonition-title { color: #3fb950; }
  .admonition-important { border-color: #a371f7; background: rgba(163,113,247,0.08); }
  .admonition-important .admonition-title { color: #a371f7; }
  .admonition-warning { border-color: #d29922; background: rgba(210,153,34,0.08); }
  .admonition-warning .admonition-title { color: #d29922; }
  .admonition-caution { border-color: #f85149; background: rgba(248,81,73,0.08); }
  .admonition-caution .admonition-title { color: #f85149; }
  table { border-collapse: collapse; margin: 0.5em 0; }
  th, td { border: 1px solid #d0d7de; padding: 6px 12px; text-align: left; }
  th { background: #f6f8fa; font-weight: 600; }
  ul, ol { margin: 0.4em 0; padding-left: 1.8em; }
  li { margin: 0.15em 0; }
  a { color: #0969da; text-decoration: none; }
  hr { border: none; border-top: 1px solid #d0d7de; margin: 1em 0; }
  img { max-width: 100%; }
</style>`;

export function getLargeCodeBlockMetadata(text, options = {}) {
    const value = typeof text === 'string' ? text : '';
    const lines = value ? value.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n') : [];
    const byteLength = new TextEncoder().encode(value).byteLength;
    const lineThreshold = Number.isFinite(options.lineThreshold) ? options.lineThreshold : LARGE_CODE_BLOCK_COLLAPSE_LINES;
    const byteThreshold = Number.isFinite(options.byteThreshold) ? options.byteThreshold : LARGE_CODE_BLOCK_COLLAPSE_BYTES;
    const shouldCollapse = lines.length > lineThreshold || byteLength > byteThreshold;
    const visibleLines = Math.min(lines.length, Number.isFinite(options.previewLines) ? options.previewLines : LARGE_CODE_BLOCK_PREVIEW_LINES);
    return {
        shouldCollapse,
        lineCount: lines.length,
        byteLength,
        visibleLines,
        omittedLines: Math.max(0, lines.length - visibleLines),
    };
}

async function copyTextToClipboard(text) {
    const value = typeof text === 'string' ? text : '';
    if (!value) return false;

    if (writeClipboardDataViaExecCommand(document, { text: value })) {
        return true;
    }

    if (await writeClipboardTextBestEffort(navigator.clipboard, value)) {
        return true;
    }

    try {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        textarea.style.pointerEvents = 'none';
        document.body.appendChild(textarea);
        textarea.select();
        textarea.setSelectionRange(0, textarea.value.length);
        const copied = document.execCommand('copy');
        document.body.removeChild(textarea);
        return copied;
    } catch {
        return false;
    }
}

async function copyMessageToClipboard(markdown) {
    const value = typeof markdown === 'string' ? markdown : '';
    if (!value) return false;

    const bodyHtml = renderMarkdown(value, null);
    const htmlDoc = `<html><head>${CLIPBOARD_STYLE}</head><body>${bodyHtml}</body></html>`;

    if (writeClipboardDataViaExecCommand(document, { text: value, html: htmlDoc })) {
        return true;
    }

    if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
        try {
            const item = new ClipboardItem({
                'text/plain': new Blob([value], { type: 'text/plain' }),
                'text/html': new Blob([htmlDoc], { type: 'text/html' }),
            });
            await navigator.clipboard.write([item]);
            return true;
        } catch (error) {
            console.warn('[post] Rich clipboard write failed, falling back to plain text copy.', error);
        }
    }

    return copyTextToClipboard(value);
}

function enhanceCodeBlocks(container) {
    if (!container) return () => {};

    const blocks = Array.from(container.querySelectorAll('pre')).filter((pre) => pre.querySelector('code'));
    if (blocks.length === 0) return () => {};

    const resetTimers = new Map();
    const cleanups = [bindSvgImageThemes(container)];

    const handleDocumentCopy = (event) => {
        const selection = window.getSelection?.();
        if (!selection || selection.isCollapsed) return;
        for (const pre of blocks) {
            if (copyPlainTextSelectionFromElement(event, { root: pre, selection })) {
                return;
            }
        }
    };

    document.addEventListener('copy', handleDocumentCopy, true);
    cleanups.push(() => document.removeEventListener('copy', handleDocumentCopy, true));

    const setButtonState = (button, state) => {
        const nextState = state || 'idle';
        button.dataset.copyState = nextState;
        if (nextState === 'success') {
            button.innerHTML = COPY_SUCCESS_SVG;
            button.setAttribute('aria-label', 'Copied');
            button.setAttribute('title', 'Copied');
            button.classList.add('is-success');
            button.classList.remove('is-error');
        } else if (nextState === 'error') {
            button.innerHTML = COPY_ERROR_SVG;
            button.setAttribute('aria-label', 'Copy failed');
            button.setAttribute('title', 'Copy failed');
            button.classList.add('is-error');
            button.classList.remove('is-success');
        } else {
            button.innerHTML = COPY_ICON_SVG;
            button.setAttribute('aria-label', 'Copy code');
            button.setAttribute('title', 'Copy code');
            button.classList.remove('is-success', 'is-error');
        }
    };

    blocks.forEach((pre) => {
        const wrapper = document.createElement('div');
        wrapper.className = 'post-code-block';
        const code = pre.querySelector('code');
        const blockMeta = getLargeCodeBlockMetadata(code?.textContent || '');
        if (blockMeta.shouldCollapse) {
            wrapper.classList.add('post-code-block-collapsed');
            wrapper.style.setProperty('--post-code-preview-lines', String(blockMeta.visibleLines));
            const toggle = document.createElement('button');
            toggle.type = 'button';
            toggle.className = 'post-code-expand-btn';
            const updateToggle = () => {
                const expanded = wrapper.classList.contains('post-code-block-expanded');
                toggle.innerHTML = expanded
                    ? `${disclosureTriangleSvgString('up')} <span>Collapse output</span>`
                    : `${disclosureTriangleSvgString('right')} <span>Expand output · ${blockMeta.lineCount.toLocaleString()} lines · ${formatFileSize(blockMeta.byteLength)}${blockMeta.omittedLines ? ` · ${blockMeta.omittedLines.toLocaleString()} hidden` : ''}</span>`;
                toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
            };
            const handleToggleClick = (event) => {
                event.preventDefault();
                event.stopPropagation();
                wrapper.classList.toggle('post-code-block-expanded');
                updateToggle();
            };
            toggle.addEventListener('click', handleToggleClick);
            updateToggle();
            wrapper.appendChild(toggle);
            cleanups.push(() => toggle.removeEventListener('click', handleToggleClick));
        }
        pre.parentNode?.insertBefore(wrapper, pre);
        wrapper.appendChild(pre);

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'post-code-copy-btn';
        setButtonState(button, 'idle');
        wrapper.appendChild(button);

        const handleCopyClick = async (event) => {
            event.preventDefault();
            event.stopPropagation();
            const code = pre.querySelector('code');
            const text = code?.hasAttribute('data-svg-source')
                ? decodeSvgSource(code.getAttribute('data-svg-source') || '')
                : code?.textContent || '';
            const ok = await copyTextToClipboard(text);
            setButtonState(button, ok ? 'success' : 'error');
            const existingTimer = resetTimers.get(button);
            if (existingTimer) clearTimeout(existingTimer);
            const timer = setTimeout(() => {
                setButtonState(button, 'idle');
                resetTimers.delete(button);
            }, CODE_COPY_RESET_MS);
            resetTimers.set(button, timer);
        };

        button.addEventListener('click', handleCopyClick);
        cleanups.push(() => {
            button.removeEventListener('click', handleCopyClick);
            const timer = resetTimers.get(button);
            if (timer) clearTimeout(timer);
            if (wrapper.parentNode) {
                wrapper.parentNode.insertBefore(pre, wrapper);
                wrapper.remove();
            }
        });
    });

    return () => {
        cleanups.forEach((cleanup) => cleanup());
    };
}

function normalizeFileRef(value) {
    const trimmed = String(value || '').trim();
    const codeWrapped = trimmed.match(/^`([^`]+)`$/);
    return (codeWrapped ? codeWrapped[1] : trimmed).trim();
}

function extractFileRefs(content) {
    if (!content) return { content, fileRefs: [] };
    const normalized = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = normalized.split('\n');
    let start = -1;
    for (let i = 0; i < lines.length; i += 1) {
        if (lines[i].trim() === 'Files:' && lines[i + 1] && /^\s*-\s+/.test(lines[i + 1])) {
            start = i;
            break;
        }
    }
    if (start === -1) return { content, fileRefs: [] };
    const refs = [];
    let end = start + 1;
    for (; end < lines.length; end += 1) {
        const line = lines[end];
        if (/^\s*-\s+/.test(line)) {
            const normalizedRef = normalizeFileRef(line.replace(/^\s*-\s+/, '').trim());
            if (normalizedRef) refs.push(normalizedRef);
        } else if (!line.trim()) {
            break;
        } else {
            break;
        }
    }
    if (refs.length === 0) return { content, fileRefs: [] };
    const before = lines.slice(0, start);
    const after = lines.slice(end);
    let cleaned = [...before, ...after].join('\n');
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
    return { content: cleaned, fileRefs: refs };
}

function extractFolderRefs(content) {
    if (!content) return { content, folderRefs: [] };
    const normalized = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = normalized.split('\n');
    let start = -1;
    for (let i = 0; i < lines.length; i += 1) {
        if (lines[i].trim() === 'Folders:' && lines[i + 1] && /^\s*-\s+/.test(lines[i + 1])) {
            start = i;
            break;
        }
    }
    if (start === -1) return { content, folderRefs: [] };
    const refs = [];
    let end = start + 1;
    for (; end < lines.length; end += 1) {
        const line = lines[end];
        if (/^\s*-\s+/.test(line)) {
            const normalizedRef = normalizeFileRef(line.replace(/^\s*-\s+/, '').trim());
            if (normalizedRef) refs.push(normalizedRef);
        } else if (!line.trim()) {
            break;
        } else {
            break;
        }
    }
    if (refs.length === 0) return { content, folderRefs: [] };
    const before = lines.slice(0, start);
    const after = lines.slice(end);
    let cleaned = [...before, ...after].join('\n');
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
    return { content: cleaned, folderRefs: refs };
}

function extractMessageRefs(content) {
    if (!content) return { content, messageRefs: [] };
    const normalized = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = normalized.split('\n');
    let start = -1;
    for (let i = 0; i < lines.length; i += 1) {
        if (lines[i].trim() === 'Referenced messages:' && lines[i + 1] && /^\s*-\s+/.test(lines[i + 1])) {
            start = i;
            break;
        }
    }
    if (start === -1) return { content, messageRefs: [] };
    const refs = [];
    let end = start + 1;
    for (; end < lines.length; end += 1) {
        const line = lines[end];
        if (/^\s*-\s+/.test(line)) {
            const val = line.replace(/^\s*-\s+/, '').trim();
            const match = val.match(/^message:(\S+)$/i);
            if (match) refs.push(match[1]);
        } else if (!line.trim()) {
            break;
        } else {
            break;
        }
    }
    if (refs.length === 0) return { content, messageRefs: [] };
    const before = lines.slice(0, start);
    const after = lines.slice(end);
    let cleaned = [...before, ...after].join('\n');
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
    return { content: cleaned, messageRefs: refs };
}

export function extractAttachmentRefs(content) {
    if (!content) return { content, attachments: [] };
    const normalized = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = normalized.split('\n');
    let start = -1;
    for (let i = 0; i < lines.length; i += 1) {
        const header = lines[i].trim();
        if ((header === 'Images:' || header === 'Attachments:') && lines[i + 1] && /^\s*-\s+/.test(lines[i + 1])) {
            start = i;
            break;
        }
    }
    if (start === -1) return { content, attachments: [] };
    const refs = [];
    let end = start + 1;
    for (; end < lines.length; end += 1) {
        const line = lines[end];
        if (/^\s*-\s+/.test(line)) {
            const raw = line.replace(/^\s*-\s+/, '').trim();
            const match = raw.match(/^attachment:([^\s)]+)\s*(?:\((.+)\))?$/i) ||
                raw.match(/^attachment:([^\s]+)\s+(.+)$/i);
            if (match) {
                const id = match[1];
                const label = (match[2] || '').trim() || id;
                refs.push({ id, label, raw });
            } else {
                refs.push({ id: null, label: raw, raw });
            }
        } else if (!line.trim()) {
            break;
        } else {
            break;
        }
    }
    if (refs.length === 0) return { content, attachments: [] };
    const before = lines.slice(0, start);
    const after = lines.slice(end);
    let cleaned = [...before, ...after].join('\n');
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
    return { content: cleaned, attachments: refs };
}

function escapeRegex(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function resolveInlineAttachments(content, attachments) {
    const usedIds = new Set();
    if (!content || attachments.length === 0) {
        return { content, usedIds };
    }

    const replaced = content.replace(/attachment:([^\s)"']+)/g, (match, rawRef, offset, source) => {
        const ref = rawRef.replace(/^\/+/, '');
        const byName = attachments.find(
            (entry) => entry.name && entry.name.toLowerCase() === ref.toLowerCase() && !usedIds.has(entry.id)
        );
        const entry = byName || attachments.find((item) => !usedIds.has(item.id));
        if (!entry) return match;
        usedIds.add(entry.id);
        const prefix = source.slice(Math.max(0, offset - 2), offset);
        if (prefix === '](') {
            return `/media/${entry.id}`;
        }
        return entry.name || 'attachment';
    });

    return { content: replaced, usedIds };
}

export function filterResolvedAttachmentItems(imageItems, fileIds, usedIds) {
    return {
        filteredImageItems: imageItems,
        filteredFileIds: fileIds.filter((id) => !usedIds.has(id)),
    };
}

function highlightHtml(html, query) {
    if (!html || !query) return html;
    const terms = String(query)
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (terms.length === 0) return html;

    const escapedTerms = terms
        .map(escapeRegex)
        .sort((a, b) => b.length - a.length);
    const pattern = new RegExp(`(${escapedTerms.join('|')})`, 'gi');
    const matcher = new RegExp(`^(${escapedTerms.join('|')})$`, 'i');

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let node;
    while ((node = walker.nextNode())) nodes.push(node);

    for (const textNode of nodes) {
        const value = textNode.nodeValue;
        if (!value || !pattern.test(value)) {
            pattern.lastIndex = 0;
            continue;
        }
        pattern.lastIndex = 0;
        const parent = textNode.parentElement;
        if (parent && parent.closest('code, pre, script, style')) continue;

        const parts = value.split(pattern).filter((part) => part !== '');
        if (parts.length === 0) continue;
        const frag = doc.createDocumentFragment();
        for (const part of parts) {
            if (matcher.test(part)) {
                const mark = doc.createElement('mark');
                mark.className = 'search-highlight-term';
                mark.textContent = part;
                frag.appendChild(mark);
            } else {
                frag.appendChild(doc.createTextNode(part));
            }
        }
        textNode.parentNode.replaceChild(frag, textNode);
    }

    return doc.body.innerHTML;
}

/**
 * Single post component
 */
export function Post({ post, onClick, onHashtagClick, onMessageRef, onScrollToMessage, agentName, agentAvatarUrl, userName, userAvatarUrl, userAvatarBackground, onDelete, isThreadReply, isThreadPrev, isThreadNext, isRemoving, highlightQuery, onFileRef, onOpenWidget, onOpenAttachmentPreview, onSaveAnnotations, onSubmitCardAction, accessory, capabilities = null }) {
    const { t } = useTranslation();
    const postCapabilities = capabilities && typeof capabilities === 'object' ? capabilities : {};
    const allowMedia = postCapabilities.media !== false;
    const allowMediaActions = allowMedia && postCapabilities.mediaActions !== false;
    const allowCards = postCapabilities.cards !== false;
    const allowWidgets = postCapabilities.widgets !== false;
    const allowAnnotations = postCapabilities.annotations !== false;
    const allowAnnotationActions = allowAnnotations && postCapabilities.annotationActions !== false;
    const allowCardActions = allowCards && postCapabilities.cardActions !== false;
    const allowWidgetActions = allowWidgets && postCapabilities.widgetActions !== false;
    const allowResourceActions = postCapabilities.resourceActions !== false;
    const allowThinking = postCapabilities.thinking !== false;
    const allowDelete = postCapabilities.delete !== false;
    const rewriteImageSrc = typeof postCapabilities.rewriteImageSrc === 'function'
        ? postCapabilities.rewriteImageSrc
        : undefined;
    const loadMediaInfo = typeof postCapabilities.loadMediaInfo === 'function'
        ? postCapabilities.loadMediaInfo
        : getMediaInfo;
    const loadThinking = typeof postCapabilities.loadThinking === 'function'
        ? postCapabilities.loadThinking
        : undefined;
    const [zoomedImage, setZoomedImage] = useState(null);
    const [annotatingImage, setAnnotatingImage] = useState(null);
    const [annotationResult, setAnnotationResult] = useState(null); // { id, url } after Done
    const [copyState, setCopyState] = useState('idle');
    const [speechPlaybackState, setSpeechPlaybackState] = useState(() => getSpeechPlaybackState());
    const [highlightPopup, setHighlightPopup] = useState(null);
    const [highlightVersion, setHighlightVersion] = useState(0);
    const [asideInput, setAsideInput] = useState(null); // { text, textOffset, rect }
    const [asideText, setAsideText] = useState('');
    const contentRef = useRef(null);
    const copyResetTimerRef = useRef(null);
    const highlightPopupRef = useRef(null);
    const highlightPopupInteractionRef = useRef(false);
    const highlightInteractionReleaseTimerRef = useRef(null);

    const data = post?.data && typeof post.data === 'object' ? post.data : {};
    const blocks = Array.isArray(data.content_blocks)
        ? data.content_blocks.filter((block) => block && typeof block === 'object' && !Array.isArray(block))
        : [];
    const mediaIds = Array.isArray(data.media_ids)
        ? data.media_ids.filter((id) => Number.isSafeInteger(id) && id > 0)
        : [];
    const linkPreviews = Array.isArray(data.link_previews)
        ? data.link_previews.filter((preview) => preview && typeof preview === 'object' && !Array.isArray(preview))
        : [];
    const peerMessageMeta = getPeerMessageMeta(blocks);
    const protectedRecoveryControl = getProtectedRecoveryControlIntent(blocks);
    const selfContinuationMeta = getSelfContinuationMeta(blocks);
    const restartHandoffMeta = getRestartHandoffMeta(blocks);
    const isAgent = data.type === 'agent_response';
    const resolvedUserName = userName || 'You';
    const isPeerAgentMessage = Boolean(!isAgent && peerMessageMeta?.sourceAgentName);
    const isSelfContinuation = Boolean(!isAgent && selfContinuationMeta);
    const displayName = !isAgent && selfContinuationMeta
        ? t('post.agentSelfResume')
        : isPeerAgentMessage
            ? peerMessageMeta.sourceAgentDisplayName
            : isAgent ? (agentName || DEFAULT_AGENT_NAME) : resolvedUserName;
    const searchChatAgentName = typeof post.chat_agent_name === 'string' ? post.chat_agent_name.trim() : '';
    const showSearchChatAgentTag = Boolean(isAgent && highlightQuery && searchChatAgentName && searchChatAgentName !== displayName);

    // Agent-authored inbound messages (peer relay and restart self-resume) use
    // the agent avatar while retaining user-message semantics for turn routing.
    const isAgentAuthoredInbound = isPeerAgentMessage || isSelfContinuation;
    const avatarInfo = (isAgent || isAgentAuthoredInbound)
        ? getAvatarInfo(isPeerAgentMessage ? peerMessageMeta.sourceAgentDisplayName : agentName, agentAvatarUrl, true)
        : getAvatarInfo(resolvedUserName, userAvatarUrl);
    const normalizedUserBackground = typeof userAvatarBackground === 'string'
        ? userAvatarBackground.trim().toLowerCase()
        : '';
    const clearUserBackground = !isAgent && avatarInfo.image
        && (normalizedUserBackground === 'clear' || normalizedUserBackground === 'transparent');
    // Keep agent avatars with transparent background when an image is set,
    // matching user avatar behavior when background is cleared.
    const clearAgentBackground = (isAgent || isAgentAuthoredInbound) && Boolean(avatarInfo.image);
    const avatarStyle = `background-color: ${(clearUserBackground || clearAgentBackground) ? 'transparent' : avatarInfo.color}`;

    const contentMeta = data.content_meta;
    const isTruncated = Boolean(contentMeta?.truncated);
    const isPreview = Boolean(contentMeta?.preview);
    const isHardTruncated = isTruncated && !isPreview;
    const truncatedInfo = isTruncated
        ? {
            originalLength: Number.isFinite(contentMeta?.original_length)
                ? contentMeta.original_length
                : (data.content ? data.content.length : 0),
            maxLength: Number.isFinite(contentMeta?.max_length) ? contentMeta.max_length : 0,
        }
        : null;

    const showPeerAgentTag = Boolean(peerMessageMeta?.sourceAgentName && isPeerAgentMessage);

    // Keep original message text even when link previews are available.
    let displayContent = protectedRecoveryControl
        ? ''
        : getDisplayContent(data.content, linkPreviews);
    displayContent = getPeerMessageDisplayContent(displayContent, blocks);
    if (restartHandoffMeta?.phase === 'notice' && restartHandoffMeta.reason) {
        displayContent = t('post.restartNotice', { reason: restartHandoffMeta.reason });
    } else if (restartHandoffMeta?.phase === 'completion') {
        displayContent = t('post.restartCompleted');
    }
    const { content: cleanedContent, fileRefs } = extractFileRefs(displayContent);
    const { content: cleanedWithFolderRefs, folderRefs } = extractFolderRefs(cleanedContent);
    const { content: cleanedWithMsgRefs, messageRefs } = extractMessageRefs(cleanedWithFolderRefs);
    const handleActivateMessageRef = (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        onMessageRef?.(post.id, post.chat_jid || null);
    };
    const handleSearchTagKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            handleActivateMessageRef(e);
        }
    };
    const { content: cleanedWithAttachments, attachments } = extractAttachmentRefs(cleanedWithMsgRefs);
    displayContent = cleanedWithAttachments;
    const directCardBlocks = allowCards ? extractCardBlocks(blocks) : [];
    const submissionBlocks = allowCards ? extractAdaptiveCardSubmissionBlocks(blocks) : [];
    const recoveryMarkerBlocks = extractRecoveryMarkerBlocks(blocks);
    const recoveryMarker = recoveryMarkerBlocks[0] || null;
    const timeoutMarkerBlocks = extractTimeoutMarkerBlocks(blocks);
    const timeoutMarker = timeoutMarkerBlocks[0] || null;
    const outcomeMarkerBlocks = extractOutcomeMarkerBlocks(blocks);
    const outcomeMarker = outcomeMarkerBlocks[0] || null;
    const agentTimingBlock = extractAgentTimingBlock(blocks);
    const postTimeTooltip = buildPostTimeTooltip(post, agentTimingBlock);
    const thinkingRef = allowThinking ? blocks.find(b => b?.type === 'thinking_ref') : undefined;
    const singleCardFallback = directCardBlocks.length === 1 && typeof directCardBlocks[0]?.fallback_text === 'string'
        ? directCardBlocks[0].fallback_text.trim()
        : '';
    const singleSubmissionFallback = submissionBlocks.length === 1
        ? buildAdaptiveCardSubmissionFallbackText(submissionBlocks[0]).trim()
        : '';
    const hideRenderedFallback = (
        Boolean(singleCardFallback) && displayContent?.trim() === singleCardFallback
    ) || (
        Boolean(singleSubmissionFallback) && displayContent?.trim() === singleSubmissionFallback
    );
    const shouldRenderContent = Boolean(displayContent) && !isHardTruncated && !hideRenderedFallback;
    const highlightQueryText = typeof highlightQuery === 'string' ? highlightQuery.trim() : '';
    const projectRepository = useChatProjectRepository(post.chat_jid, post.project_repository);
    const renderedHtml = useMemo(() => {
        if (!displayContent || hideRenderedFallback) return '';
        const baseHtml = renderMarkdown(displayContent, onHashtagClick, { rewriteImageSrc, projectRepository });
        return highlightQueryText ? highlightHtml(baseHtml, highlightQueryText) : baseHtml;
    }, [displayContent, hideRenderedFallback, highlightQueryText, onHashtagClick, rewriteImageSrc, projectRepository]);

    const markdownCopyPayload = useMemo(() => buildPostMarkdownCopyPayload(post), [post]);
    const speechSupported = useMemo(() => isSpeechSynthesisSupported(), []);
    const speakableText = useMemo(() => buildSpeakablePostText(post), [post]);
    const isSpeakingThisPost = Boolean(speechPlaybackState.speaking && speechPlaybackState.activePostId === post.id);

    const handleImageClick = (e, mediaId, mimeType) => {
        e.stopPropagation();
        if (!allowMediaActions) return;
        const src = getMediaUrl(mediaId);
        if (allowAnnotationActions && canAnnotate()) {
            setAnnotatingImage({ src, mimeType });
        } else {
            setZoomedImage(src);
        }
    };

    const handleAttachmentPreview = (attachment) => {
        onOpenAttachmentPreview?.(attachment);
    };

    const handleHighlightSelection = useCallback(async (color) => {
        if (!highlightPopup) {
            console.warn('[post-highlight] No highlightPopup state — handler called with stale closure');
            return;
        }
        const highlight = buildHighlightFromSelectionSnapshot(highlightPopup, color);
        if (!highlight) return;
        highlightPopupInteractionRef.current = false;
        // Clear popup and selection immediately
        setHighlightPopup(null);
        window.getSelection()?.removeAllRanges();
        // Apply highlight locally first for immediate visual feedback
        if (contentRef.current) {
            applyHighlightsToElement(contentRef.current, [highlight]);
        }
        // Persist to DB
        try {
            const updated = await persistHighlight(post.id, post.chat_jid, data.annotations, highlight, onSaveAnnotations);
            data.annotations = updated;
        } catch (err) {
            console.warn('[post-highlight] Failed to persist highlight:', err);
        }
        setHighlightVersion((v) => v + 1);
    }, [highlightPopup, post.id, post.chat_jid, data]);

    const handleAddAside = useCallback(() => {
        if (!highlightPopup) return;
        highlightPopupInteractionRef.current = false;
        setAsideInput({ text: highlightPopup.text, textOffset: highlightPopup.textOffset, rect: highlightPopup.rect });
        setAsideText('');
        setHighlightPopup(null);
        window.getSelection()?.removeAllRanges();
    }, [highlightPopup]);

    const holdHighlightPopup = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        highlightPopupInteractionRef.current = true;
        if (highlightInteractionReleaseTimerRef.current) {
            clearTimeout(highlightInteractionReleaseTimerRef.current);
        }
        highlightInteractionReleaseTimerRef.current = setTimeout(() => {
            highlightInteractionReleaseTimerRef.current = null;
            highlightPopupInteractionRef.current = false;
            setHighlightPopup(null);
        }, 1500);
    }, []);

    const releaseHighlightPopup = useCallback((event) => {
        event.stopPropagation();
        if (highlightInteractionReleaseTimerRef.current) {
            clearTimeout(highlightInteractionReleaseTimerRef.current);
            highlightInteractionReleaseTimerRef.current = null;
        }
        setTimeout(() => {
            highlightPopupInteractionRef.current = false;
        }, 0);
    }, []);

    const handleSaveAside = useCallback(async () => {
        if (!asideInput || !asideText.trim()) return;
        const aside: PostAside = {
            type: 'aside',
            text: asideInput.text,
            textOffset: asideInput.textOffset,
            note: asideText.trim(),
        };
        setAsideInput(null);
        setAsideText('');
        try {
            const updated = await persistAside(post.id, post.chat_jid, data.annotations, aside, onSaveAnnotations);
            data.annotations = updated;
        } catch (err) {
            console.warn('[post-aside] Failed to persist aside:', err);
        }
        setHighlightVersion((v) => v + 1);
    }, [asideInput, asideText, post.id, post.chat_jid, data]);

    const handleCancelAside = useCallback(() => {
        setAsideInput(null);
        setAsideText('');
    }, []);

    const handleAnnotationSave = useCallback((result) => {
        setAnnotatingImage(null);
        if (result?.id) {
            setAnnotationResult({ id: result.id, url: getMediaUrl(result.id) });
        }
    }, []);

    const handleAnnotationSend = useCallback(async () => {
        if (!annotationResult?.id) return;
        // Attach the annotated image to the compose box so user can add commentary
        window.dispatchEvent(new CustomEvent('piclaw:compose-media-attach', {
            detail: { mediaId: annotationResult.id, mediaUrl: annotationResult.url },
        }));
        setAnnotationResult(null);
    }, [annotationResult]);

    const handleAnnotationDiscard = useCallback(() => {
        setAnnotationResult(null);
    }, []);

    const handleDeleteClick = (e) => {
        e.stopPropagation();
        onDelete?.(post);
    };

    const handleCopyMarkdownClick = async (e) => {
        e.stopPropagation();
        const ok = await copyMessageToClipboard(markdownCopyPayload);
        setCopyState(ok ? 'success' : 'error');
        if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current);
        copyResetTimerRef.current = setTimeout(() => {
            copyResetTimerRef.current = null;
            setCopyState('idle');
        }, CODE_COPY_RESET_MS);
    };

    const handleSpeakClick = (e) => {
        e.stopPropagation();
        if (isSpeakingThisPost) {
            stopSpeechPlayback();
            return;
        }
        speakPostText(post.id, speakableText);
    };

    // Separate images from files using content_blocks info
    const imageItems: Array<{ id: number; annotations?: unknown; mimeType?: string }> = [];
    const fileIds = [];
    const attachmentEntries = [];
    const resourceLinks = [];
    const resources = [];
    const generatedWidgets = [];
    const textAnnotations = [];
    let mediaIndex = 0;

    if (blocks.length > 0) {
        blocks.forEach((block) => {
            if (allowAnnotations && block?.type === 'text' && block.annotations) {
                textAnnotations.push(block.annotations);
            }
            if (allowWidgets && block?.type === 'generated_widget') {
                generatedWidgets.push(block);
            } else if (allowMedia && block?.type === 'resource_link') {
                resourceLinks.push(block);
            } else if (allowMedia && block?.type === 'resource') {
                resources.push(block);
            } else if (allowMedia && block?.type === 'file') {
                const id = mediaIds[mediaIndex++];
                if (id) {
                    fileIds.push(id);
                    attachmentEntries.push({ id, name: block?.name || block?.filename || block?.title });
                }
            } else if (allowMedia && (block?.type === 'image' || !block?.type)) {
                const id = mediaIds[mediaIndex++];
                if (id) {
                    const mimeType =
                        typeof block?.mime_type === 'string' ? block.mime_type : undefined;
                    imageItems.push({ id, annotations: block?.annotations, mimeType });
                    attachmentEntries.push({ id, name: block?.name || block?.filename || block?.title });
                }
            }
        });
    } else if (allowMedia && mediaIds.length > 0) {
        const treatAsFiles = attachments.length > 0;
        mediaIds.forEach((id, index) => {
            const ref = attachments[index] || null;
            attachmentEntries.push({ id, name: ref?.label || null });
            if (treatAsFiles) {
                fileIds.push(id);
            } else {
                imageItems.push({ id, annotations: null });
            }
        });
    }

    if (attachments.length > 0) {
        attachments.forEach((ref) => {
            if (!ref?.id) return;
            const match = attachmentEntries.find((entry) => String(entry.id) === String(ref.id));
            if (match && !match.name) {
                match.name = ref.label;
            }
        });
    }

    const { content: resolvedContent, usedIds } = resolveInlineAttachments(displayContent, attachmentEntries);
    displayContent = resolvedContent;
    const { filteredImageItems, filteredFileIds } = filterResolvedAttachmentItems(imageItems, fileIds, usedIds);

    const attachmentPills = attachments.length > 0
        ? attachments.map((ref, idx) => ({
            id: ref.id || `attachment-${idx + 1}`,
            label: ref.label || `attachment-${idx + 1}`,
        }))
        : attachmentEntries.map((entry, idx) => ({
            id: entry.id,
            label: entry.name || `attachment-${idx + 1}`,
        }));

    // Extract adaptive card blocks from content_blocks
    const cardBlocks = useMemo(() => allowCards ? extractCardBlocks(blocks) : [], [allowCards, blocks]);
    const cardSubmissionBlocks = useMemo(() => allowCards ? extractAdaptiveCardSubmissionBlocks(blocks) : [], [allowCards, blocks]);

    // Stable identity key for card blocks so the render effect only fires when
    // a card's identity or lifecycle state actually changes — not on every
    // parent re-render caused by unrelated SSE events.  This prevents the
    // card DOM (and all user input state) from being torn down and rebuilt
    // while the user is filling in a form.
    const cardBlocksKey = useMemo(() => {
        return cardBlocks.map((b) => `${b.card_id}:${b.state}`).join('|');
    }, [cardBlocks]);

    // Render mermaid diagrams and enhance code blocks after content is mounted
    useEffect(() => {
        if (!contentRef.current) return undefined;
        renderMermaidDiagrams(contentRef.current, { rewriteImageSrc });
        return enhanceCodeBlocks(contentRef.current);
    }, [renderedHtml, rewriteImageSrc]);

    // Re-apply saved text highlights and asides after content renders. Family
    // can display persisted annotations while mutation gestures remain disabled.
    useEffect(() => {
        if (!allowAnnotations || !contentRef.current) return;
        // Remove any previously inserted pills/asides/marks before re-applying
        contentRef.current.querySelectorAll('.post-aside-pill, .post-aside-content, mark.post-highlight').forEach((el) => {
            // Unwrap marks back to text
            if (el.tagName === 'MARK') {
                const parent = el.parentNode;
                while (el.firstChild) parent?.insertBefore(el.firstChild, el);
                el.remove();
            } else {
                el.remove();
            }
        });
        // Normalize text nodes after unwrapping
        contentRef.current.normalize();

        const annotations = Array.isArray(data.annotations) ? data.annotations : [];
        const highlights = extractHighlightsFromAnnotations(annotations);
        if (highlights.length > 0) applyHighlightsToElement(contentRef.current, highlights);
        const asides = extractAsidesFromAnnotations(annotations);
        if (asides.length > 0) applyAsidesToElement(contentRef.current, asides);

        // Add click-to-remove only when the adapter authorizes annotation writes.
        const marks = allowAnnotationActions
            ? contentRef.current.querySelectorAll('mark.post-highlight')
            : [];
        marks.forEach((mark) => {
            (mark as HTMLElement).style.cursor = 'pointer';
            (mark as HTMLElement).title = 'Click to remove highlight';
            mark.addEventListener('click', async (e) => {
                e.stopPropagation();
                const markText = mark.textContent ?? '';
                const idx = annotations.findIndex((a: any) =>
                    a?.type === 'highlight' && a?.text === markText
                );
                if (idx >= 0) {
                    try {
                        const updated = await removeAnnotationAtIndex(post.id, post.chat_jid, annotations, idx, onSaveAnnotations);
                        data.annotations = updated;
                        setHighlightVersion((v) => v + 1);
                    } catch (err) {
                        console.warn('[post] Failed to remove highlight:', err);
                    }
                }
            }, { once: true });
        });
    }, [allowAnnotations, allowAnnotationActions, renderedHtml, highlightVersion]);

    // Listen for text selection to show highlight popup
    useEffect(() => {
        if (!allowAnnotationActions) return;
        const el = contentRef.current;
        if (!el) return;
        const onSelectionChange = () => {
            const info = getSelectionInElement(el);
            if (info && info.text.length > 0) {
                setHighlightPopup(info);
            } else {
                if (highlightPopupInteractionRef.current) return;
                setHighlightPopup(null);
            }
        };
        // Also listen for pointerup/touchend on the post content to detect
        // selection completion (selectionchange can fire too early on iPad)
        const onPointerUp = () => {
            setTimeout(onSelectionChange, 50);
        };
        const unsubscribeSelectionChanges = subscribePostSelectionChanges(el, onSelectionChange);
        el.addEventListener('pointerup', onPointerUp);
        el.addEventListener('touchend', onPointerUp);
        return () => {
            unsubscribeSelectionChanges();
            el.removeEventListener('pointerup', onPointerUp);
            el.removeEventListener('touchend', onPointerUp);
            if (highlightInteractionReleaseTimerRef.current) {
                clearTimeout(highlightInteractionReleaseTimerRef.current);
                highlightInteractionReleaseTimerRef.current = null;
            }
            highlightPopupInteractionRef.current = false;
        };
    }, [allowAnnotationActions, renderedHtml]);

    const highlightPopupIsDocked = highlightPopup
        ? isIOSDevice() || hasCoarseAnnotationPointer(typeof window === 'undefined' ? null : window)
        : false;
    const highlightPopupPlacement = highlightPopup && !highlightPopupIsDocked
        ? resolveHighlightPopupPlacement(
            highlightPopup.rect,
            { width: window.innerWidth, height: window.innerHeight },
            {
                width: highlightPopupRef.current?.offsetWidth || 180,
                height: highlightPopupRef.current?.offsetHeight || 40,
            },
        )
        : null;

    useLayoutEffect(() => {
        if (!highlightPopup || highlightPopupIsDocked || !highlightPopupRef.current) return;
        const placement = resolveHighlightPopupPlacement(
            highlightPopup.rect,
            { width: window.innerWidth, height: window.innerHeight },
            {
                width: highlightPopupRef.current.offsetWidth,
                height: highlightPopupRef.current.offsetHeight,
            },
        );
        highlightPopupRef.current.style.left = `${placement.left}px`;
        highlightPopupRef.current.style.top = `${placement.top}px`;
    }, [highlightPopup, highlightPopupIsDocked]);

    useEffect(() => {
        return subscribeSpeechPlayback((nextState) => {
            setSpeechPlaybackState(nextState);
        });
    }, []);

    useEffect(() => () => {
        if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current);
    }, []);

    // Render adaptive cards into their containers.
    // The effect depends on cardBlocksKey (card_id + state) rather than the
    // full cardBlocks array, so unrelated parent re-renders won't destroy
    // the card DOM and reset user inputs.
    const cardContainerRef = useRef(null);
    useEffect(() => {
        if (!cardContainerRef.current || cardBlocks.length === 0) return;
        const container = cardContainerRef.current;
        container.innerHTML = '';
        for (const block of cardBlocks) {
            const cardEl = document.createElement('div');
            container.appendChild(cardEl);
            renderAdaptiveCard(cardEl, block, {
                readOnly: !allowCardActions,
                rewriteResourceUrl: rewriteImageSrc,
                onAction: allowCardActions ? async (action) => {
                    if (action.type === 'Action.OpenUrl') {
                        const safeUrl = sanitizeUrl(action.url || '');
                        if (!safeUrl) throw new Error('Invalid URL');
                        window.open(safeUrl, '_blank', 'noopener,noreferrer');
                        return;
                    }

                    if (action.type === 'Action.Submit') {
                        const submit = onSubmitCardAction ?? submitAdaptiveCardAction;
                        const response = await submit({
                            post_id: post.id,
                            thread_id: data.thread_id || post.id,
                            chat_jid: post.chat_jid || null,
                            card_id: block.card_id,
                            action: {
                                type: action.type,
                                title: action.title || '',
                                data: action.data,
                            },
                        });
                        presentProviderAuth(response, submit);
                        return;
                    }

                    console.warn('[post] unsupported adaptive card action:', action.type, action);
                } : undefined,
            }).then((rendered) => {
                if (!rendered) cardEl.textContent = block.fallback_text || 'Card failed to render.';
            }).catch((err) => {
                console.error('[post] adaptive card render error:', err);
                cardEl.textContent = block.fallback_text || 'Card failed to render.';
            });
        }
    }, [allowCardActions, cardBlocksKey, post.id, rewriteImageSrc]);

    const silentRecoveryPlaceholder = Boolean(
        isAgent
        && outcomeMarker?.kind === 'recovery'
        && outcomeMarker?.severity === 'info'
        && !shouldRenderContent
        && mediaIds.length === 0
        && directCardBlocks.length === 0
        && submissionBlocks.length === 0
    );
    if (protectedRecoveryControl || silentRecoveryPlaceholder) return null;

    return html`
        <div id=${`post-${post.id}`} class="post ${isAgent ? 'agent-post' : ''} ${isSelfContinuation ? 'self-continuation-post' : ''} ${isThreadReply ? 'thread-reply' : ''} ${isThreadPrev ? 'thread-prev' : ''} ${isThreadNext ? 'thread-next' : ''} ${isRemoving ? 'removing' : ''} ${allowMediaActions ? '' : 'post-media-readonly'}" onClick=${onClick}>
            <div class="post-avatar ${(isAgent || isAgentAuthoredInbound) ? 'agent-avatar' : ''} ${avatarInfo.image ? 'has-image' : ''}" style=${avatarStyle}>
                ${avatarInfo.image ? html`<img src=${avatarInfo.image} alt=${displayName} />` : avatarInfo.letter}
            </div>
            <div class="post-body">
                <div class="post-actions">
                    ${isAgent && speechSupported && speakableText && html`
                        <button
                            class=${`post-action-btn post-speak-btn${isSpeakingThisPost ? ' is-active' : ''}`}
                            type="button"
                            title=${isSpeakingThisPost ? 'Stop reading aloud' : 'Read aloud'}
                            aria-label=${isSpeakingThisPost ? 'Stop reading aloud' : 'Read aloud'}
                            onClick=${handleSpeakClick}
                        >
                            ${isSpeakingThisPost
                                ? html`<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="6" y="6" width="12" height="12" rx="2"></rect></svg>`
                                : html`<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M11 5 6 9H3v6h3l5 4z"></path><path d="M15.5 8.5a5 5 0 0 1 0 7"></path><path d="M18 6a8.5 8.5 0 0 1 0 12"></path></svg>`}
                        </button>
                    `}
                    <button
                        class=${`post-action-btn post-copy-btn${copyState === 'success' ? ' is-success' : copyState === 'error' ? ' is-error' : ''}`}
                        type="button"
                        title=${copyState === 'success' ? 'Copied' : copyState === 'error' ? 'Copy failed' : 'Copy message'}
                        aria-label=${copyState === 'success' ? 'Copied' : copyState === 'error' ? 'Copy failed' : 'Copy message'}
                        onClick=${handleCopyMarkdownClick}
                        disabled=${!markdownCopyPayload}
                    >
                        ${copyState === 'success'
                            ? html`<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M20 6L9 17l-5-5"></path></svg>`
                            : copyState === 'error'
                                ? html`<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9"></circle><path d="M9 9l6 6M15 9l-6 6"></path></svg>`
                                : html`<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="9" y="9" width="10" height="10" rx="2"></rect><path d="M7 15H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1"></path></svg>`}
                    </button>
                    ${allowDelete && onDelete && html`<button
                        class="post-action-btn post-delete-btn"
                        type="button"
                        title=${t('post.deleteMessage')}
                        aria-label=${t('post.deleteMessage')}
                        onClick=${handleDeleteClick}
                    >
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>`}
                </div>
                <div class="post-meta">
                    <span class="post-author">${displayName}</span>
                    ${showPeerAgentTag && html`
                        <span
                            class="post-chat-agent-tag"
                            title=${`From ${peerMessageMeta?.sourceChatJid || ''}`.trim()}
                        >
                            @${peerMessageMeta?.sourceAgentName}
                        </span>
                    `}
                    ${showSearchChatAgentTag && html`
                        <span
                            class="post-chat-agent-tag"
                            role="button"
                            tabIndex="0"
                            style="cursor:pointer"
                            title=${`Open ${searchChatAgentName} and reference this message`}
                            onClick=${handleActivateMessageRef}
                            onKeyDown=${handleSearchTagKeyDown}
                        >
                            @${searchChatAgentName}
                        </span>
                    `}
                    <a
                        class="post-time"
                        href=${`#msg-${post.id}`}
                        title=${postTimeTooltip}
                        aria-label=${postTimeTooltip}
                        onClick=${handleActivateMessageRef}
                    >${formatTime(post.timestamp)}</a>
                    ${recoveryMarker && html`
                        <span
                            class="post-recovery-chip"
                            title=${formatRecoveryChipTooltip(recoveryMarker)}
                        >
                            recovered
                        </span>
                    `}
                    ${timeoutMarker && html`
                        <span
                            class="post-recovery-chip post-timeout-chip"
                            title=${formatTimeoutChipTooltip(timeoutMarker)}
                        >
                            timeout
                        </span>
                    `}
                    ${outcomeMarker && html`
                        <span
                            class=${`post-recovery-chip post-outcome-chip post-outcome-chip-${String(outcomeMarker.severity || 'warning')}${outcomeMarker.kind === 'tool_budget' ? ' post-outcome-chip-tool-budget' : ''}`}
                            title=${String(outcomeMarker.label || outcomeMarker.kind || 'issue')}
                        >
                            ${String(outcomeMarker.label || outcomeMarker.kind || 'issue')}
                        </span>
                    `}
                </div>
                ${outcomeMarker && html`
                    <${OutcomePill} marker=${outcomeMarker} />
                `}
                ${isAgent && thinkingRef && html`<${ThinkingVisibilityPill} messageId=${post.id} chatJid=${post.chat_jid} lines=${thinkingRef.lines || 0} durationMs=${thinkingRef.duration_ms || 0} loadThinking=${loadThinking} />`}
                ${isHardTruncated && truncatedInfo && html`
                    <div class="post-content truncated">
                        <div class="truncated-title">${t('post.tooLarge')}</div>
                        <div class="truncated-meta">
                            Original length: ${formatCount(truncatedInfo.originalLength)} chars
                            ${truncatedInfo.maxLength ? html` • Display limit: ${formatCount(truncatedInfo.maxLength)} chars` : ''}
                        </div>
                    </div>
                `}
                ${isPreview && truncatedInfo && html`
                    <div class="post-content preview">
                        <div class="truncated-title">${t('post.previewTruncated')}</div>
                        <div class="truncated-meta">
                            Showing first ${formatCount(truncatedInfo.maxLength)} of ${formatCount(truncatedInfo.originalLength)} chars. Download full text below.
                        </div>
                    </div>
                `}
                ${(fileRefs.length > 0 || folderRefs.length > 0 || messageRefs.length > 0 || attachmentPills.length > 0) && html`
                    <div class="post-file-refs">
                        ${messageRefs.map((id) => {
                            const scrollToRef = (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (onScrollToMessage) {
                                    onScrollToMessage(id, post.chat_jid || null);
                                } else {
                                    const el = document.getElementById('post-' + id);
                                    if (el) {
                                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        el.classList.add('post-highlight');
                                        setTimeout(() => el.classList.remove('post-highlight'), 2000);
                                    }
                                }
                            };
                            return html`
                                <a href=${`#msg-${id}`} class="post-msg-pill-link" onClick=${scrollToRef}>
                                    <${FilePill}
                                        prefix="post"
                                        label=${'msg:' + id}
                                        title=${'Message ' + id}
                                        icon="message"
                                        onClick=${scrollToRef}
                                    />
                                </a>
                            `;
                        })}
                        ${fileRefs.map((ref) => {
                            const label = ref.split('/').pop() || ref;
                            if (/^uploads\/upload-[a-zA-Z0-9-]{16,90}\/[^/]+$/.test(ref) && isSafeUploadFilename(label)) {
                                return html`<a class="post-file-pill" href=${`/workspace/raw?path=${encodeURIComponent(ref)}&download=1`} download title=${ref}>${label}</a>`;
                            }
                            return html`
                                <${FilePill}
                                    prefix="post"
                                    label=${label}
                                    title=${ref}
                                    onClick=${() => onFileRef?.(ref)}
                                />
                            `;
                        })}
                        ${folderRefs.map((ref) => {
                            const label = ref.split('/').pop() || ref;
                            return html`
                                <${FilePill}
                                    prefix="post"
                                    label=${label}
                                    title=${ref}
                                    icon="folder"
                                />
                            `;
                        })}
                        ${attachmentPills.map((attachment) => html`
                            <${AttachmentPill}
                                key=${attachment.id}
                                attachment=${attachment}
                                onPreview=${handleAttachmentPreview}
                                allowActions=${allowMediaActions}
                                loadInfo=${loadMediaInfo}
                            />
                        `)}
                    </div>
                `}
                ${shouldRenderContent && html`
                    <div
                        ref=${contentRef}
                        class="post-content"
                        dangerouslySetInnerHTML=${{ __html: renderedHtml }}
                        onClick=${(e) => {
                            if (e.target.classList.contains('hashtag')) {
                                e.preventDefault();
                                e.stopPropagation();
                                const tag = e.target.dataset.hashtag;
                                if (tag) onHashtagClick?.(tag);
                            } else if (allowMediaActions && e.target.tagName === 'IMG') {
                                e.preventDefault();
                                e.stopPropagation();
                                setZoomedImage(e.target.src);
                            }
                        }}
                    />
                `}
                ${cardBlocks.length > 0 && html`
                    <div ref=${cardContainerRef} class="post-adaptive-cards" />
                `}
                ${cardSubmissionBlocks.length > 0 && html`
                    <div class="post-adaptive-card-submissions">
                        ${cardSubmissionBlocks.map((block, idx) => {
                            const meta = describeAdaptiveCardSubmission(block);
                            const submissionKey = `${block.card_id}-${idx}`;
                            return html`
                                <div key=${submissionKey} class="adaptive-card-submission-receipt">
                                    <div class="adaptive-card-submission-header">
                                        <span class="adaptive-card-submission-icon" aria-hidden="true">✓</span>
                                        <div class="adaptive-card-submission-title-wrap">
                                            <span class="adaptive-card-submission-title">${t('post.submitted')}</span>
                                            <span class="adaptive-card-submission-title-action">${meta.title}</span>
                                        </div>
                                    </div>
                                    ${meta.fields.length > 0 && html`
                                        <div class="adaptive-card-submission-fields">
                                            ${meta.fields.map((field) => html`
                                                <span class="adaptive-card-submission-field" title=${`${field.key}: ${field.value}`}>
                                                    <span class="adaptive-card-submission-field-key">${field.key}</span>
                                                    <span class="adaptive-card-submission-field-sep">:</span>
                                                    <span class="adaptive-card-submission-field-value">${field.value}</span>
                                                </span>
                                            `)}
                                        </div>
                                    `}
                                    <div class="adaptive-card-submission-meta">
                                        Submitted ${formatTimestamp(meta.submittedAt)}
                                    </div>
                                </div>
                            `;
                        })}
                    </div>
                `}
                ${generatedWidgets.length > 0 && html`
                    <div class="generated-widget-launches">
                        ${generatedWidgets.map((block, idx) => html`
                            <${GeneratedWidgetLaunch}
                                key=${block.widget_id || block.id || `${post.id}-widget-${idx}`}
                                block=${block}
                                post=${post}
                                onOpenWidget=${onOpenWidget}
                                allowOpen=${allowWidgetActions}
                            />
                        `)}
                    </div>
                `}
                ${textAnnotations.length > 0 && html`
                    ${textAnnotations.map((annotations, idx) => html`
                        <${AnnotationsBadge} key=${idx} annotations=${annotations} />
                    `)}
                `}
                ${filteredImageItems.length > 0 && html`
                    <div class="media-preview">
                        ${filteredImageItems.map(({ id, mimeType }) => {
                            const isSvg = typeof mimeType === 'string' && mimeType.toLowerCase().startsWith('image/svg');
                            const imageSrc = isSvg ? getMediaUrl(id) : getThumbnailUrl(id);
                            return html`
                                <img
                                    key=${id}
                                    src=${imageSrc}
                                    alt="Media"
                                    loading="lazy"
                                    decoding="async"
                                    onClick=${allowMediaActions ? (e) => handleImageClick(e, id, mimeType) : undefined}
                                />
                            `;
                        })}
                    </div>
                `}
                ${filteredImageItems.length > 0 && html`
                    ${filteredImageItems.map(({ annotations }, idx) => html`
                        ${annotations && html`<${AnnotationsBadge} key=${idx} annotations=${annotations} />`}
                    `)}
                `}
                ${filteredFileIds.length > 0 && html`
                    <div class="file-attachments">
                        ${filteredFileIds.map(id => html`
                            <${FileAttachment} key=${id} mediaId=${id} onPreview=${handleAttachmentPreview} allowActions=${allowMediaActions} loadInfo=${loadMediaInfo} />
                        `)}
                    </div>
                `}
                ${resourceLinks.length > 0 && html`
                    <div class="resource-links">
                        ${resourceLinks.map((block, idx) => html`
                            <div key=${idx}>
                                <${ResourceLinkBlock} block=${block} allowOpen=${allowResourceActions} />
                                <${AnnotationsBadge} annotations=${block.annotations} />
                            </div>
                        `)}
                    </div>
                `}
                ${resources.length > 0 && html`
                    <div class="resource-embeds">
                        ${resources.map((block, idx) => html`
                            <div key=${idx}>
                                <${ResourceBlock} block=${block} allowDownload=${allowResourceActions} />
                                <${AnnotationsBadge} annotations=${block.annotations} />
                            </div>
                        `)}
                    </div>
                `}
                ${linkPreviews.length > 0 && html`
                    <div class="link-previews">
                        ${linkPreviews.map((preview, i) => html`
                            <${LinkPreview} key=${i} preview=${preview} rewriteImageSrc=${rewriteImageSrc} />
                        `)}
                    </div>
                `}
                ${accessory}
            </div>
        </div>
        ${zoomedImage && html`<${ImageModal} src=${zoomedImage} onClose=${() => setZoomedImage(null)} />`}
        ${annotatingImage && html`
            <div class="post-inline-annotator">
                <${ImageAnnotator}
                    src=${annotatingImage.src}
                    mimeType=${annotatingImage.mimeType}
                    onSave=${handleAnnotationSave}
                    onCancel=${() => setAnnotatingImage(null)}
                />
            </div>
        `}
        ${annotationResult && html`
            <div class="post-annotation-result">
                <img src=${annotationResult.url} alt="Annotated" class="post-annotation-preview" />
                <div class="post-annotation-actions">
                    <button class="post-annotation-send-btn" onClick=${handleAnnotationSend}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                        Send
                    </button>
                    <button class="post-annotation-discard-btn" onClick=${handleAnnotationDiscard}>${t('post.discard')}</button>
                </div>
            </div>
        `}
        ${highlightPopup && html`
            <div
                ref=${highlightPopupRef}
                class=${`post-highlight-popup${highlightPopupIsDocked ? ' post-highlight-popup-docked' : ''}`}
                style=${highlightPopupPlacement
                    ? `left:${highlightPopupPlacement.left}px; top:${highlightPopupPlacement.top}px`
                    : undefined}
                onPointerDown=${holdHighlightPopup}
                onPointerUp=${releaseHighlightPopup}
                onPointerCancel=${releaseHighlightPopup}
            >
                ${HIGHLIGHT_COLORS.map((c) => html`
                    <button
                        key=${c.name}
                        class="post-highlight-color-btn"
                        style="background:${c.value}; border:1px solid rgba(128,128,128,0.3)"
                        onClick=${(e) => { e.preventDefault(); e.stopPropagation(); handleHighlightSelection(c.value); }}
                        title=${`Highlight ${c.name}`}
                    />
                `)}
                <button
                    class="post-aside-add-btn"
                    onClick=${(e) => { e.preventDefault(); e.stopPropagation(); handleAddAside(); }}
                    title=${t('post.addNote')}
                >✎</button>
            </div>
        `}
        ${asideInput && html`
            <div
                class="post-aside-input-popup"
                style="position:fixed; left:${Math.max(8, asideInput.rect.left)}px; top:${asideInput.rect.bottom + 8}px; z-index:100"
            >
                <textarea
                    class="post-aside-textarea"
                    placeholder=${t('post.addNotePlaceholder')}
                    value=${asideText}
                    onInput=${(e) => setAsideText(e.currentTarget.value)}
                    onKeyDown=${(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSaveAside(); }
                        if (e.key === 'Escape') handleCancelAside();
                    }}
                    ref=${(el) => el && requestAnimationFrame(() => el.focus())}
                />
                <div class="post-aside-input-actions">
                    <button class="post-aside-save-btn" onClick=${handleSaveAside} disabled=${!asideText.trim()}>${t('post.save')}</button>
                    <button class="post-aside-cancel-btn" onClick=${handleCancelAside}>${t('post.cancel')}</button>
                </div>
            </div>
        `}

    `;
}
